import React, { useState, useEffect } from 'react';
import { message, Modal, Select, Breadcrumb, Button } from 'antd';
import { ExclamationCircleOutlined, DashboardOutlined, PlusSquareOutlined } from '@ant-design/icons';
import ItemTable from '../../components/store/ItemTable';
import ItemModal from '../../components/store/ItemModal';
import { useGetAllItems, useCreateItem, useUpdateItem, useDeleteItem, useSearchItems } from '../../hooks/store/itemQuery';
import { useGetAllStoreDepartments } from '../../hooks/store/storeDepartmentQuery';
import { useGetAllCategories } from '../../hooks/store/categoryQuery';
import { useGetAllStores } from '../../hooks/store/storeQuery';
import Header from '../../components/dashboard/header';
import { Link } from 'react-router-dom';
import { MdOutlineInventory2, MdOutlineCategory } from 'react-icons/md';
import { FaWarehouse } from 'react-icons/fa';
import { BiPackage } from 'react-icons/bi';

const { confirm } = Modal;
const { Option } = Select;

const ItemPage = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [departmentName, setDepartmentName] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { loading: storesLoading, stores } = useGetAllStores(refreshFlag);
  const { loading: departmentsLoading, departments } = useGetAllStoreDepartments(selectedStore, refreshFlag);
  const { loading: categoriesLoading, categories } = useGetAllCategories(refreshFlag);

  // Use search hook if search keyword is provided, otherwise use regular items hook
  const { loading: searchLoading, items: searchItems, pagination: searchPagination } = useSearchItems(searchKeyword, page, pageSize, refreshFlag);
  const { loading: itemsLoading, items: regularItems } = useGetAllItems(selectedDepartment, selectedCategory, refreshFlag);

  const loading = searchKeyword ? searchLoading : itemsLoading;
  const items = searchKeyword ? searchItems : regularItems;

  const createItem = useCreateItem();
  const updateItem = useUpdateItem();
  const deleteItem = useDeleteItem();

  useEffect(() => {
    if (stores.length > 0 && !selectedStore) {
      setSelectedStore(stores[0].id);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (departments.length > 0 && !selectedDepartment) {
      setSelectedDepartment(departments[0].id);
      setDepartmentName(departments[0].name);
    }
  }, [departments, selectedDepartment]);

  useEffect(() => {
    if (selectedDepartment) {
      const department = departments.find(d => d.id === selectedDepartment);
      if (department) {
        setDepartmentName(department.name);
      }
    }
  }, [selectedDepartment, departments]);

  const handleStoreChange = (value) => {
    setSelectedStore(value);
    setSelectedDepartment(null);
    setDepartmentName('');
  };

  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPage(0); // Reset to first page when searching
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current - 1); // API uses 0-based indexing
    setPageSize(pagination.pageSize);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    confirm({
      title: 'Are you sure you want to delete this item?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteItem(record.id);
          message.success('Item deleted successfully');
          setRefreshFlag(!refreshFlag);
        } catch (error) {
          message.error('Failed to delete item');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingItem) {
        await updateItem(editingItem.id, values);
        message.success('Item updated successfully');
      } else {
        await createItem(values);
        message.success('Item created successfully');
      }
      setModalVisible(false);
      setRefreshFlag(!refreshFlag);
    } catch (error) {
      message.error('Failed to save item');
    }
  };

  return (
    <>
      <div className="main-container">
        <Header />
        <div className="main-body">
          <div className="info-bar">
            <Breadcrumb>
              <Breadcrumb.Item>
                <DashboardOutlined className="dashboard-icon" />
                <Link to={"/dashboard"}>Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <MdOutlineInventory2 />
                <Link to={"/dashboard/store"}>Store</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <BiPackage />
                <Link to="/dashboard/item">Items</Link>
              </Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ display: 'flex', gap: '1em', alignItems: 'center' }}>
              <Select
                placeholder="Select Store"
                style={{ width: 200 }}
                value={selectedStore}
                onChange={handleStoreChange}
                loading={storesLoading}
              >
                {stores.map(store => (
                  <Option key={store.id} value={store.id}>{store.name}</Option>
                ))}
              </Select>
              <Select
                placeholder="Select Department"
                style={{ width: 200 }}
                value={selectedDepartment}
                onChange={handleDepartmentChange}
                loading={departmentsLoading}
                disabled={!selectedStore}
              >
                {departments.map(department => (
                  <Option key={department.id} value={department.id}>{department.name}</Option>
                ))}
              </Select>
              <Button
                type="primary"
                style={{ backgroundColor: "green", color: "#FFFFFF" }}
                onClick={handleAdd}
                icon={<PlusSquareOutlined />}
              >
                Create Item
              </Button>
            </div>
          </div>
          <div className="table-section">
            <ItemTable
              items={items}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={handleAdd}
              loading={loading}
              departmentName={departmentName}
              categories={categories}
              onCategoryChange={handleCategoryChange}
              onSearch={handleSearch}
              onTableChange={handleTableChange}
              pagination={searchKeyword ? {
                current: searchPagination.current,
                pageSize: searchPagination.pageSize,
                total: searchPagination.total,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ['5', '10', '25', '50'],
              } : undefined}
            />
          </div>
        </div>
      </div>
      <ItemModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleSubmit}
        initialValues={editingItem}
        title={editingItem ? 'Edit Item' : 'Add Item'}
        departments={departments}
        categories={categories}
      />
    </>
  );
};

export default ItemPage;
